import {Component, OnDestroy, OnInit} from '@angular/core';
import { Location } from '@angular/common';

import {AppService} from './app.service';
import { Token, UserDetails } from './app.model';
import {CacheService} from './cache.service';
import {ActivatedRoute, Router, NavigationStart, ActivationEnd, NavigationEnd} from '@angular/router';
//import {VersionCheckService} from './version-check.service';
//import {MixpanelService} from './services/mixpanel.service';
import {environment} from '../environments/environment';
import { catchError, exhaustMap, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, from, of, Subject, timer } from 'rxjs';
import { CookieService } from 'ngx-cookie';
import { NS_AppService, NS_Enums } from './shared/namespaces';
import { includes as _includes, split as _split, get as _get } from 'lodash-es';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AuthService } from './auth/auth.service';
import { PosthogService } from './services/posthog/posthog.service';
import { isLocalStorageAvailable } from './shared/helpers/helpers';
import { SentryService } from './services/sentry/sentry.service';
import { RedirectionService } from './services/redirection/redirection.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { FirebaseAuthStateService } from './shared/services/firebase-auth-state.service';
import {
  handleFirebasePermissionError,
  isFirebasePermissionError,
} from './shared/helpers/firebase-permission-error.handler';
// import { CookieService } from 'ngx-cookie-service';


const MAINTENANCE_POLL_MS = 10000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  userSession: any;
  maintenance: any = false;
  currentUrl;
  location: Location;
  private destroy$ = new Subject<boolean>();

  // Arrow fn keeps `this` stable for removeEventListener; force-refresh prevents stale-token permission errors on tab return.
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    // TODO: [follow-up] Gate force-refresh on elapsed-time-since-last-refresh to avoid
    // unnecessary token refreshes on every tab return. Tracked as a separate follow-up
    // outside the scope of the permission-error fix (CU-86d2k08wf).
    this.firebaseAuthStateService.getValidToken().pipe(takeUntil(this.destroy$)).subscribe({
      error: (error) => {
        const isAuthenticated = this.authService.isLoggedIn;
        if (isFirebasePermissionError(error)) {
          handleFirebasePermissionError(error, this.snackBar, isAuthenticated);
        } else {
          this.sentryService.captureException(
            error instanceof Error
              ? error
              : new Error(error?.message || 'Failed to refresh Firebase token on tab return'),
            { firebase_error_code: error?.code, is_authenticated: isAuthenticated }
          );
        }
      }
    });
  };
  constructor( private cacheService: CacheService, private appService: AppService, private route: ActivatedRoute, private router: Router,
    private afAuth: AngularFireAuth,
    private authService: AuthService,
    private posthogService: PosthogService,
    private sentryService: SentryService,
    private redirectService: RedirectionService,
    private firebaseAuthStateService: FirebaseAuthStateService,
    private snackBar: MatSnackBar,
    location: Location, private cookie: CookieService) {
      this.location = location;
  }

  private COOKIE_CONSENT_STATUS = "cookie_consent_status";
  private COOKIE_CONSENT_TIME = "cookie_consent_time";
  uncommonBaseWord: string = this.appService.getUncommonBaseWord();
  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.posthogService.init();
    this.validateLoginState()
    this.appService.maintenanceMode$.pipe(
      tap((value) => {
        this.maintenance = value;
      }),
      // The "We'll be back soon" card used to stay until the user reloaded and signed in again.
      // While it is up, poll the status endpoint and clear it once the backend answers.
      switchMap((value) => value ? this.pollUntilBackendRecovers() : EMPTY),
      takeUntil(this.destroy$)
    ).subscribe(() => this.appService.maintenanceMode$.next(false));
    // this.theversionCheckService.initVersionCheck(environment.versionCheckURL);
    //console.log("HELLO_WORLD2", this.cacheService.getObject<Token>('saras_auth_key') );

    // Bootstrap: userData$ uses skipWhile so won't emit synchronously; read cache directly for initial Sentry context.
    const userDetails = this.cacheService.getObject<UserDetails>('user_details');
    this.syncSentryUser(userDetails);
    //  this.mixpanel.init();
    this.cacheService.userData$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((userDataState: { user?: UserDetails | null }) => {
        this.userSession = userDataState.user?.userId;
        this.syncSentryUser(userDataState.user);
    });
    // console.log(JSON.stringify(userDetails))
    this.appService.initAndStoreCB();

    this.router.events.subscribe({
      next: () => {
        this.currentUrl = this.location.path()
      }
    })

    this.cacheService.initSharedCookieStatus();
    this.cacheService.sharedCookieState$.subscribe((value)=>{
      if(value === 'INIT'){
        return;
      }
      if(!this.appService.authValidationInProgress){
        if(!value && this.authService.isLoggedIn){
          console.log('sharedCookieState LOGGED OUT');
          this.afAuth.signOut().then(() => {
            this.appService.clearUserDetails();
            this.cacheService.clear();
            this.router.navigate(['/login']);
            window['fcWidget']?.destroy();
          });
        }else if(!this.authService.isLoggedIn){
          // this.router.navigate(['/auth']);
        }
      }
    });

  }

  ngDoCheck() {
    const saras_auth_key = this.cacheService.getObject<Token>('saras_auth_key');
    if(saras_auth_key && isLocalStorageAvailable()){
      localStorage.setItem(this.COOKIE_CONSENT_STATUS, "true");
      localStorage.setItem(this.COOKIE_CONSENT_TIME, new Date().toISOString());
    }
  }

  ngOnDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.destroy$.next(true);
  }

  private syncSentryUser(user?: UserDetails | null): void {
    if (user?.userId || user?.company?.companyId) {
      this.sentryService.setUserInfo({
        userId: user.userId,
        companyId: user.company?.companyId,
      });
    }
  }

  /** Emits once, when `services/status` first succeeds; checks every `MAINTENANCE_POLL_MS`. */
  private pollUntilBackendRecovers() {
    return timer(MAINTENANCE_POLL_MS, MAINTENANCE_POLL_MS).pipe(
      exhaustMap(() => from(this.appService.getServiceStatus()).pipe(
        map(() => true),
        catchError(() => of(false))
      )),
      filter((healthy) => healthy),
      take(1)
    );
  }

  validateLoginState() {
    if (!this.cacheService.hasValidAuthKey()) {
      setTimeout(() => {

        // Get the previous URL
        const previousUrl = this.router.url;

        // Extract the base path, ignoring query parameters
        const basePath = _get(_split(_get(_split(previousUrl, '?'), [0]), '/'), [1]); // Splits the path by '?' and takes the first part
    
        // Define the URLs for which redirection should not occur
        const excludedUrls = NS_AppService.OPEN_ROUTES;
    
        // Check if the previous URL is not one of the excluded ones
        if (!_includes(excludedUrls, basePath) && !this.appService.authValidationInProgress) {
          this.redirectService.redirectToLogin();
        }
      }, 100)
    }
  }

  
  
}
